import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Beef, ShoppingBag, Gift, CreditCard, ClipboardList, User, LogIn,
  Download, Clock, MapPin, Phone, MessageCircle, ChevronRight, Star
} from 'lucide-react';
import axios from 'axios';
import PushNotificationManager from '@/components/PushNotificationManager';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8000';
const API = `${BACKEND_URL}/api`;

const DEFAULT_CATEGORIES = [
  { id: 'vitella', name: 'Vitella', icon: '🥩', visible: true },
  { id: 'maiale', name: 'Maiale', icon: '🐖', visible: true },
  { id: 'pollo', name: 'Pollo', icon: '🐔', visible: true },
  { id: 'preparati', name: 'Preparati', icon: '🍔', visible: true }
];

const DEFAULT_SECTION_ORDER = ['hero', 'quick_actions', 'categories', 'offers', 'about', 'contact'];

const dayNames = {
  mon: 'Lunedì', tue: 'Martedì', wed: 'Mercoledì', thu: 'Giovedì',
  fri: 'Venerdì', sat: 'Sabato', sun: 'Domenica'
};

export default function LandingPage({ user }) {
  const [settings, setSettings] = useState({
    business_name: 'Macelleria Borgese',
    tagline: 'Qualità e tradizione',
    hero_title: 'Carne scelta, pronta quando vuoi',
    hero_description: 'Ordina dal telefono e scegli ritiro o consegna.',
    hero_image: '',
    about_title: 'Tradizione e qualità',
    about_text: 'Prodotti selezionati e preparati con cura.',
    about_image: '',
    footer_text: 'Qualità e tradizione',
    logo_url: '',
    primary_color: '#9B1B30',
    secondary_color: '#D4AF37',
    address: '',
    phone: '',
    whatsapp: '',
    show_whatsapp: true,
    email: '',
    maps_url: '',
    opening_hours: {},
    product_categories: DEFAULT_CATEGORIES,
    home_show_hero: true,
    home_show_quick_actions: true,
    home_show_categories: true,
    home_show_offers: true,
    home_show_about: true,
    home_show_contact: true,
    home_show_install_button: true,
    home_hero_button_text: 'Ordina ora',
    home_quick_actions_title: 'Accessi rapidi',
    home_categories_title: 'Scegli una categoria',
    home_offers_title: 'Offerte del momento',
    home_about_button_text: 'Scopri di più',
    home_section_order: DEFAULT_SECTION_ORDER
  });
  const [offers, setOffers] = useState([]);
  const [showInstallHelp, setShowInstallHelp] = useState(false);
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [isStandalone, setIsStandalone] = useState(false);

  const primaryColor = settings.primary_color || '#9B1B30';
  const secondaryColor = settings.secondary_color || '#D4AF37';

  useEffect(() => {
    const standalone = window.matchMedia('(display-mode: standalone)').matches || window.navigator.standalone;
    setIsStandalone(Boolean(standalone));

    const onPrompt = event => {
      event.preventDefault();
      setDeferredPrompt(event);
    };
    window.addEventListener('beforeinstallprompt', onPrompt);
    return () => window.removeEventListener('beforeinstallprompt', onPrompt);
  }, []);

  useEffect(() => {
    let mounted = true;
    const load = async () => {
      try {
        const [settingsResponse, offersResponse] = await Promise.allSettled([
          axios.get(`${API}/settings?t=${Date.now()}`, {
            headers: { 'Cache-Control': 'no-cache', Pragma: 'no-cache' }
          }),
          axios.get(`${API}/offers`)
        ]);

        if (!mounted) return;
        if (settingsResponse.status === 'fulfilled') {
          setSettings(previous => ({ ...previous, ...settingsResponse.value.data }));
        }
        if (offersResponse.status === 'fulfilled') {
          setOffers((offersResponse.value.data || []).filter(item => item.active !== false).slice(0, 6));
        }
      } catch {
        // The page remains usable with local defaults.
      }
    };

    load();
    const interval = window.setInterval(load, 30000);
    window.addEventListener('focus', load);
    return () => {
      mounted = false;
      window.clearInterval(interval);
      window.removeEventListener('focus', load);
    };
  }, []);

  const categories = useMemo(() => {
    const configured = Array.isArray(settings.product_categories) && settings.product_categories.length
      ? settings.product_categories
      : DEFAULT_CATEGORIES;
    return configured
      .map((item, index) => ({
        ...item,
        visible: item.visible !== false,
        order: Number.isFinite(Number(item.order)) ? Number(item.order) : index
      }))
      .filter(item => item.visible)
      .sort((a, b) => a.order - b.order);
  }, [settings.product_categories]);

  const sections = useMemo(() => {
    const order = Array.isArray(settings.home_section_order) && settings.home_section_order.length
      ? settings.home_section_order
      : DEFAULT_SECTION_ORDER;
    return [...new Set([...order, ...DEFAULT_SECTION_ORDER])];
  }, [settings.home_section_order]);

  const installApp = async () => {
    if (deferredPrompt) {
      deferredPrompt.prompt();
      await deferredPrompt.userChoice;
      setDeferredPrompt(null);
      return;
    }
    setShowInstallHelp(true);
  };

  const dashboardLink = user
    ? (user.role === 'admin' || user.role === 'employee' ? '/admin' : '/dashboard')
    : '/login';

  const quickActions = [
    {
      label: 'Ordina',
      sublabel: 'Scegli i prodotti',
      icon: ShoppingBag,
      to: '/ordina',
      enabled: true
    },
    {
      label: 'Offerte',
      sublabel: 'Scopri le promozioni',
      icon: Gift,
      to: '/offers',
      enabled: true
    },
    {
      label: user ? 'I miei ordini' : 'Accedi',
      sublabel: user ? 'Controlla lo stato' : 'Ordini e fidelity',
      icon: user ? ClipboardList : LogIn,
      to: user ? '/dashboard' : '/login',
      enabled: true
    },
    {
      label: 'Fidelity',
      sublabel: 'Punti e premi',
      icon: CreditCard,
      to: user ? '/dashboard' : '/login',
      enabled: true
    }
  ];

  const heroSection = settings.home_show_hero !== false && (
    <section className="px-3 pt-3 sm:px-6 sm:pt-8">
      <div
        className="relative max-w-6xl mx-auto overflow-hidden rounded-[28px] min-h-[330px] sm:min-h-[450px] flex items-end"
        style={{ backgroundColor: primaryColor }}
      >
        {settings.hero_image && (
          <img
            src={settings.hero_image}
            alt=""
            className="absolute inset-0 w-full h-full object-cover"
            loading="eager"
          />
        )}
        <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/35 to-black/10" />
        <div className="relative z-10 p-5 sm:p-10 max-w-2xl text-white">
          <span className="inline-flex items-center gap-2 bg-white/15 backdrop-blur px-3 py-1.5 rounded-full text-xs font-bold mb-4">
            <Star size={14} style={{ color: secondaryColor }} />
            {settings.tagline}
          </span>
          <h2 className="font-serif text-3xl sm:text-5xl font-bold leading-tight">
            {settings.hero_title}
          </h2>
          <p className="mt-3 text-sm sm:text-lg text-white/85 leading-relaxed max-w-xl">
            {settings.hero_description}
          </p>
          <Link
            to="/ordina"
            className="mt-5 min-h-[54px] w-full sm:w-auto px-7 rounded-2xl font-bold inline-flex items-center justify-center gap-2"
            style={{ backgroundColor: secondaryColor, color: '#241B12' }}
          >
            <ShoppingBag size={20} />
            {settings.home_hero_button_text || 'Ordina ora'}
          </Link>
        </div>
      </div>
    </section>
  );

  const quickSection = settings.home_show_quick_actions !== false && (
    <section className="px-3 py-5 sm:px-6 sm:py-8">
      <div className="max-w-6xl mx-auto">
        <h3 className="text-lg sm:text-2xl font-serif font-bold mb-3" style={{ color: primaryColor }}>
          {settings.home_quick_actions_title || 'Accessi rapidi'}
        </h3>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          {quickActions.map(action => {
            const Icon = action.icon;
            return (
              <Link
                key={action.label}
                to={action.to}
                className="bg-white rounded-2xl border border-[#E8E2D8] p-4 min-h-[126px] flex flex-col justify-between shadow-sm active:scale-[0.98] transition-transform"
              >
                <span className="w-11 h-11 rounded-xl flex items-center justify-center text-white" style={{ backgroundColor: primaryColor }}>
                  <Icon size={22} />
                </span>
                <div>
                  <strong className="block text-[#29231F]">{action.label}</strong>
                  <span className="text-xs text-[#746C64]">{action.sublabel}</span>
                </div>
              </Link>
            );
          })}
        </div>
      </div>
    </section>
  );

  const categoriesSection = settings.home_show_categories !== false && (
    <section className="py-5 sm:py-8">
      <div className="max-w-6xl mx-auto">
        <div className="px-3 sm:px-6 flex items-end justify-between gap-3 mb-3">
          <h3 className="text-lg sm:text-2xl font-serif font-bold" style={{ color: primaryColor }}>
            {settings.home_categories_title || 'Scegli una categoria'}
          </h3>
          <Link to="/ordina" className="text-sm font-bold flex items-center gap-1" style={{ color: primaryColor }}>
            Tutte <ChevronRight size={16} />
          </Link>
        </div>
        <div className="flex gap-3 overflow-x-auto px-3 sm:px-6 pb-2 snap-x">
          {categories.map(category => (
            <Link
              key={category.id}
              to={`/ordina?categoria=${encodeURIComponent(category.id)}`}
              className="snap-start flex-shrink-0 w-[112px] sm:w-[145px] min-h-[116px] rounded-2xl bg-white border border-[#E8E2D8] p-3 flex flex-col items-center justify-center text-center shadow-sm"
            >
              <span className="text-3xl sm:text-4xl">{category.icon || '📦'}</span>
              <strong className="mt-2 text-sm sm:text-base text-[#29231F] line-clamp-2">{category.name}</strong>
            </Link>
          ))}
        </div>
      </div>
    </section>
  );

  const offersSection = settings.home_show_offers !== false && (
    <section className="px-3 py-5 sm:px-6 sm:py-8">
      <div className="max-w-6xl mx-auto">
        <div className="flex items-end justify-between gap-3 mb-3">
          <h3 className="text-lg sm:text-2xl font-serif font-bold" style={{ color: primaryColor }}>
            {settings.home_offers_title || 'Offerte del momento'}
          </h3>
          <Link to="/offers" className="text-sm font-bold flex items-center gap-1" style={{ color: primaryColor }}>
            Vedi tutte <ChevronRight size={16} />
          </Link>
        </div>

        {offers.length ? (
          <div className="flex gap-3 overflow-x-auto pb-2 snap-x">
            {offers.map(offer => (
              <Link
                key={offer.id}
                to="/offers"
                className="snap-start flex-shrink-0 w-[78vw] max-w-[310px] overflow-hidden rounded-2xl bg-white border border-[#E8E2D8] shadow-sm"
              >
                {offer.image_url ? (
                  <img src={offer.image_url} alt={offer.title || offer.name} className="w-full h-36 object-cover" loading="lazy" />
                ) : (
                  <div className="h-36 flex items-center justify-center" style={{ backgroundColor: `${primaryColor}12` }}>
                    <Gift size={42} style={{ color: primaryColor }} />
                  </div>
                )}
                <div className="p-4">
                  <span className="text-xs font-bold uppercase" style={{ color: primaryColor }}>Offerta</span>
                  <h4 className="font-bold text-lg text-[#29231F] line-clamp-2">{offer.title || offer.name}</h4>
                  {offer.description && <p className="text-sm text-[#746C64] line-clamp-2 mt-1">{offer.description}</p>}
                </div>
              </Link>
            ))}
          </div>
        ) : (
          <Link to="/offers" className="rounded-2xl border border-dashed border-[#CFC6BB] bg-white p-5 flex items-center gap-4">
            <span className="w-12 h-12 rounded-xl flex items-center justify-center text-white" style={{ backgroundColor: primaryColor }}>
              <Gift size={24} />
            </span>
            <div>
              <strong className="block">Scopri le offerte</strong>
              <span className="text-sm text-[#746C64]">Controlla le promozioni disponibili.</span>
            </div>
          </Link>
        )}
      </div>
    </section>
  );

  const aboutSection = settings.home_show_about !== false && (
    <section className="px-3 py-5 sm:px-6 sm:py-8">
      <div className="max-w-6xl mx-auto bg-white rounded-[28px] border border-[#E8E2D8] overflow-hidden sm:grid sm:grid-cols-2">
        {settings.about_image && (
          <img src={settings.about_image} alt="" className="w-full h-48 sm:h-full object-cover" loading="lazy" />
        )}
        <div className="p-5 sm:p-8 flex flex-col justify-center">
          <span className="text-xs uppercase font-bold tracking-wider" style={{ color: primaryColor }}>Chi siamo</span>
          <h3 className="font-serif text-2xl sm:text-3xl font-bold mt-1 text-[#29231F]">{settings.about_title}</h3>
          <p className="text-sm sm:text-base text-[#6F675F] leading-relaxed mt-3">{settings.about_text}</p>
          <a href="#contatti" className="mt-5 font-bold inline-flex items-center gap-1" style={{ color: primaryColor }}>
            {settings.home_about_button_text || 'Scopri di più'} <ChevronRight size={17} />
          </a>
        </div>
      </div>
    </section>
  );

  const contactSection = settings.home_show_contact !== false && (
    <section id="contatti" className="px-3 py-5 sm:px-6 sm:py-8">
      <div className="max-w-6xl mx-auto grid gap-3 sm:grid-cols-3">
        <div className="sm:col-span-2 bg-white rounded-2xl border border-[#E8E2D8] p-5">
          <h3 className="font-serif text-xl font-bold flex items-center gap-2" style={{ color: primaryColor }}>
            <Clock size={21} /> Orari della macelleria
          </h3>
          <div className="mt-4 grid sm:grid-cols-2 gap-x-6">
            {Object.entries(dayNames).map(([key, label]) => {
              const hours = settings.opening_hours?.[key] || {};
              const closed = !hours.open;
              return (
                <div key={key} className="py-2 border-b border-[#EEE8E0] flex justify-between gap-3 text-sm">
                  <strong>{label}</strong>
                  <span className={closed ? 'text-red-600' : 'text-[#6F675F] text-right'}>
                    {closed ? 'Chiuso' : `${hours.open}-${hours.close}${hours.afternoon_open ? ` / ${hours.afternoon_open}-${hours.afternoon_close}` : ''}`}
                  </span>
                </div>
              );
            })}
          </div>
        </div>

        <div className="bg-white rounded-2xl border border-[#E8E2D8] p-5">
          <h3 className="font-serif text-xl font-bold" style={{ color: primaryColor }}>Contatti</h3>
          <div className="mt-4 grid gap-2">
            {settings.phone && (
              <a href={`tel:${settings.phone}`} className="min-h-[48px] px-3 rounded-xl bg-[#F7F3ED] flex items-center gap-3">
                <Phone size={19} style={{ color: primaryColor }} />
                <span className="font-semibold text-sm">{settings.phone}</span>
              </a>
            )}
            {settings.show_whatsapp !== false && settings.whatsapp && (
              <a href={`https://wa.me/${settings.whatsapp.replace(/\D/g, '')}`} target="_blank" rel="noreferrer" className="min-h-[48px] px-3 rounded-xl bg-green-50 flex items-center gap-3">
                <MessageCircle size={19} className="text-green-700" />
                <span className="font-semibold text-sm">Scrivici su WhatsApp</span>
              </a>
            )}
            {settings.address && (
              <a href={settings.maps_url || `https://maps.google.com/?q=${encodeURIComponent(settings.address)}`} target="_blank" rel="noreferrer" className="min-h-[48px] px-3 rounded-xl bg-[#F7F3ED] flex items-center gap-3">
                <MapPin size={19} style={{ color: primaryColor }} />
                <span className="font-semibold text-sm line-clamp-2">{settings.address}</span>
              </a>
            )}
          </div>
        </div>
      </div>
    </section>
  );

  const sectionMap = {
    hero: heroSection,
    quick_actions: quickSection,
    categories: categoriesSection,
    offers: offersSection,
    about: aboutSection,
    contact: contactSection
  };

  return (
    <div className="min-h-screen bg-[#F7F3ED] pb-[calc(env(safe-area-inset-bottom,0px)+20px)]">
      <PushNotificationManager user={user} primaryColor={primaryColor} />

      <header
        className="sticky top-0 z-40 bg-white/95 backdrop-blur border-b border-[#E8E2D8]"
        style={{ paddingTop: 'max(env(safe-area-inset-top, 0px), 8px)' }}
      >
        <div className="max-w-6xl mx-auto min-h-[64px] px-3 sm:px-6 flex items-center justify-between gap-3">
          <Link to="/" className="flex items-center gap-2 min-w-0">
            {settings.logo_url ? (
              <img src={settings.logo_url} alt={settings.business_name} className="w-10 h-10 object-contain rounded-xl" />
            ) : (
              <span className="w-10 h-10 rounded-xl flex items-center justify-center text-white" style={{ backgroundColor: primaryColor }}>
                <Beef size={22} />
              </span>
            )}
            <div className="min-w-0">
              <h1 className="font-serif font-bold text-base sm:text-xl truncate" style={{ color: primaryColor }}>{settings.business_name}</h1>
              <p className="text-[11px] sm:text-xs text-[#746C64] truncate">{settings.tagline}</p>
            </div>
          </Link>

          <div className="flex items-center gap-2">
            {!isStandalone && settings.home_show_install_button !== false && (
              <button
                type="button"
                onClick={installApp}
                className="w-10 h-10 sm:w-auto sm:px-4 rounded-xl border flex items-center justify-center gap-2"
                style={{ borderColor: primaryColor, color: primaryColor }}
                aria-label="Installa app"
              >
                <Download size={18} />
                <span className="hidden sm:inline font-bold">Installa app</span>
              </button>
            )}
            <Link
              to={dashboardLink}
              className="w-10 h-10 sm:w-auto sm:px-4 rounded-xl text-white flex items-center justify-center gap-2 font-bold"
              style={{ backgroundColor: primaryColor }}
              aria-label={user ? 'Area personale' : 'Accedi'}
            >
              {user ? <User size={18} /> : <LogIn size={18} />}
              <span className="hidden sm:inline">{user ? 'Area personale' : 'Accedi'}</span>
            </Link>
          </div>
        </div>
      </header>

      <main>
        {sections.map(section => <div key={section}>{sectionMap[section] || null}</div>)}
      </main>

      <footer className="mt-5 px-4 py-8 text-center text-white" style={{ backgroundColor: '#282321' }}>
        <div className="flex items-center justify-center gap-2">
          {settings.logo_url ? (
            <img src={settings.logo_url} alt="" className="w-8 h-8 object-contain" />
          ) : (
            <Beef size={22} style={{ color: secondaryColor }} />
          )}
          <strong className="font-serif">{settings.business_name}</strong>
        </div>
        <p className="text-sm text-white/65 mt-2">{settings.footer_text}</p>
      </footer>

      {showInstallHelp && (
        <div className="fixed inset-0 z-50 bg-black/60 p-4 flex items-end sm:items-center justify-center" onClick={() => setShowInstallHelp(false)}>
          <div className="w-full max-w-sm rounded-3xl bg-white p-5" onClick={event => event.stopPropagation()}>
            <h3 className="font-serif text-xl font-bold" style={{ color: primaryColor }}>Installa l’app</h3>
            <p className="text-sm text-[#6F675F] mt-2">
              Su iPhone apri il menu Condividi e scegli “Aggiungi alla schermata Home”.
              Su Android apri il menu del browser e scegli “Installa app”.
            </p>
            <button type="button" onClick={() => setShowInstallHelp(false)} className="mt-5 w-full min-h-[48px] rounded-xl text-white font-bold" style={{ backgroundColor: primaryColor }}>
              Ho capito
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
